/**
 * Menu Flattener
 * 
 * Converts Strapi header API response (flat array of nav-bar and sub-items components)
 * into hierarchical menu structure for React rendering
 * 
 * Algorithm:
 * - If nav-bar component followed by sub-items, nest as children
 * - If nav-bar follows nav-bar, no children
 * - Convert null CustomLink values to empty string
 */

import { convertCustomerTypeDisplay } from './userTypeMapper';
import { SOURCE_URL } from '../config';

export interface StrapiMenuItem {
  id: number;
  __component: string;
  MenuItems?: {
    id: number;
    Name: string;
    CustomLink: string | null;
    CustomerTypeDisplay: string[];
    ForceLogin: boolean;
    NewWindow: boolean;
  };
  SubItems?: {
    id: number;
    Name: string;
    CustomLink: string | null;
    CustomerTypeDisplay: string[];
    ForceLogin: boolean;
    NewWindow: boolean;
  };
}

export interface FlattenedMenuItem {
  id: string;
  label: string;
  url: string;
  target: string;
  displayFor: string[];
  forceLogin: boolean;
  children: FlattenedMenuItem[];
}

/**
 * Flatten Strapi header menu API response into hierarchical structure
 * 
 * @param apiResponse - Array of menu components from Strapi API
 * @returns Flattened menu array with nested children
 */
export function flattenMenu(apiResponse: StrapiMenuItem[]): FlattenedMenuItem[] {
  if (!apiResponse || apiResponse.length === 0) {
    return [];
  }

  const flattenedMenu: FlattenedMenuItem[] = [];

  for (let i = 0; i < apiResponse.length; i++) {
    const currentItem = apiResponse[i];

    // Only process nav-bar components as menu items
    if (currentItem.__component === 'menu.nav-bar' && currentItem.MenuItems) {
      const menuData = currentItem.MenuItems;
      
      const menuItem: FlattenedMenuItem = {
        id: `menu-item-${menuData.id}`,
        label: menuData.Name,
        url: menuData.CustomLink || '', // Convert null to empty string
        target: menuData.NewWindow ? '_blank' : '_self',
        displayFor: convertCustomerTypeDisplay(menuData.CustomerTypeDisplay),
        forceLogin: menuData.ForceLogin,
        children: []
      };

      // Collect all consecutive sub-items that follow this nav-bar
      let j = i + 1;
      while (j < apiResponse.length && apiResponse[j].__component === 'menu.sub-items') {
        const subItemBlock = apiResponse[j];
        const subItemData = subItemBlock.SubItems || subItemBlock.MenuItems;

        if (subItemData) {
          menuItem.children.push({
            id: `submenu-item-${subItemData.id}`,
            label: subItemData.Name,
            url: subItemData.CustomLink || '', // Convert null to empty string
            target: subItemData.NewWindow ? '_blank' : '_self',
            displayFor: convertCustomerTypeDisplay(subItemData.CustomerTypeDisplay),
            forceLogin: subItemData.ForceLogin,
            children: []
          });
        }

        j++;
      }

      // Advance index to last processed sub-item (if any)
      i = j - 1;

      flattenedMenu.push(menuItem);
    }
  }

  return flattenedMenu;
}

/**
 * Process Strapi header API full response
 * Returns structured menu data for all header sections
 */
export interface ProcessedHeaderMenus {
  desktopPrimary: FlattenedMenuItem[];
  mobilePrimary: FlattenedMenuItem[];
  mobileSecondary: FlattenedMenuItem[];
  watchDesktop: FlattenedMenuItem[];
  watchMobile: FlattenedMenuItem[];
  logo?: string;
}

export function processHeaderResponse(apiData: any): ProcessedHeaderMenus {
  const attributes = apiData?.data?.attributes;

  if (!attributes) {
    return {
      desktopPrimary: [],
      mobilePrimary: [],
      mobileSecondary: [],
      watchDesktop: [],
      watchMobile: []
    };
  }

  return {
    desktopPrimary: flattenMenu(attributes.HeaderMenu || []),
    mobilePrimary: flattenMenu(attributes.HeaderMenu || []), // Mobile uses same HeaderMenu
    mobileSecondary: flattenMenu(attributes.MobileHeaderMenuLower || []),
    watchDesktop: flattenMenu(attributes.WatchMenu || []),
    watchMobile: flattenMenu(attributes.WatchMenu || []), // Mobile uses same WatchMenu
    logo: attributes.Logo?.data?.attributes?.url? SOURCE_URL + attributes.Logo?.data?.attributes?.url : '',
  };
}
